import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3203';
import { luHandleAppiError } from '../U3203Service';
import { doGetProvider, type U3202AiModelChild, type U3202ParameterSchemaDefinition } from '../../u3202AiProvider/U3202Service';
import type { U3202ModelCapabilities } from '../../u3202AiProvider/utils/U3202CapabilitiesTypes';
import { U3202_EMPTY_CAPABILITIES } from '../../u3202AiProvider/utils/U3202CapabilitiesTypes';

export interface U3203MissionModelContext {
  providerName: string;
  modelName: string;
  parameterSchema: U3202ParameterSchemaDefinition;
  defaultParameters: Record<string, unknown>;
  capabilities: U3202ModelCapabilities;
}

export function useU3203MissionModelContext(providerId: string | null, modelId: string | null) {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const [context, setContext] = useState<U3203MissionModelContext | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!providerId || !modelId) {
      setContext(null);
      return;
    }
    setLoading(true);
    try {
      const response = await doGetProvider(u2000AxiosAuthenticated, providerId);
      const provider = response.data?.result;
      const model = (provider?.models ?? []).find((m: U3202AiModelChild) => m.id === modelId && m.active);
      if (!model || !provider) {
        setContext(null);
        return;
      }
      setContext({
        providerName: provider.name,
        modelName: model.name,
        parameterSchema: model.parameterSchema ?? {},
        defaultParameters: model.defaultParameters ?? {},
        capabilities: model.capabilities ?? U3202_EMPTY_CAPABILITIES,
      });
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setContext(null);
    } finally {
      setLoading(false);
    }
  }, [modelId, providerId, t, toast, u2000AxiosAuthenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  return { context, loading, reload: load };
}
